import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  useTicket,
  useTicketMessages,
  useTicketEvents,
  useTeamMembers,
  useMarkTicketNotificationsRead,
  useActiveAwayPeriods,
  useGmailInboxes,
  type TeamMember,
} from '@/lib/queries';
import { setLastVisitedSlug } from '@/lib/inbox-scope';
import { AwayBanner } from '@/components/AwayBadge';
import { useTicketAction } from '@/lib/use-ticket-action';
import { useKeyboardShortcuts } from '@/lib/use-keyboard-shortcuts';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageThreadSkeleton, TicketDetailHeaderSkeleton } from '@/components/Skeletons';
import { TicketActivityPanel, TicketWatchersPanel } from '@/components/TicketActivitySidebar';
import { NotesPanel } from '@/components/NotesPanel';
import { ContactTicketsPanel } from '@/components/ContactTicketsPanel';
import { ThreadAttachmentsPanel } from '@/components/ThreadAttachmentsPanel';
import { ReplyComposer } from '@/components/ReplyComposer';
import { FindInThread } from '@/components/FindInThread';
import { UserAvatar } from '@/components/UserAvatar';
import { RelativeTime } from '@/components/RelativeTime';
import {
  assigneeName,
  nameInitials,
  statusColor,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import { ArrowLeft, GitMerge, Hash, Mail, Moon, Pencil, Sparkles } from 'lucide-react';
import { MessageBody } from '@/components/MessageBody';
import { useTicketPresence } from '@/lib/use-ticket-presence';
import { useWorkspacePresenceContext } from '@/lib/workspace-presence-context';
import { useAuth } from '@/lib/auth-guard';
import { useAccess } from '@/lib/use-access';
import { SlaCountdown } from '@/components/SlaCountdown';
import { ThreadSummary } from '@/components/ThreadSummary';
import { PendingReplies } from '@/components/PendingReplies';
import { TicketStickyBar } from '@/components/TicketStickyBar';
import { AutomatedSenderControl } from '@/components/AutomatedSenderControl';
import { PriorityMenu } from '@/components/PriorityMenu';
import { TicketListNav } from '@/components/TicketListNav';
import { useTicketNav } from '@/lib/use-ticket-nav';
import { useTicketReads } from '@/lib/use-unread';
import { advancesList } from '@/lib/ticket-nav';

export function TicketDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const ticketQ = useTicket(id);
  const messagesQ = useTicketMessages(id);
  const eventsQ = useTicketEvents(id);
  const { data: teamMembers } = useTeamMembers();
  const markTicketNotificationsRead = useMarkTicketNotificationsRead();
  const { teamMember } = useAuth();

  // Per-inbox permissions (migration 078) for this ticket's inbox. The
  // server refuses regardless; these only hide the controls and shortcuts
  // the role doesn't have.
  const access = useAccess();
  const ticketInboxId = ticketQ.data?.inbox_id ?? null;
  const canReply = access.canIn(ticketInboxId, 'tickets.reply');
  const canClaimHere = access.canAction(ticketInboxId, 'claim', {
    actorSlackId: teamMember?.slack_user_id,
    assigneeSlackId: ticketQ.data?.assignee_slack_id,
  });
  const canResolveHere = access.canIn(ticketInboxId, 'tickets.status');

  // Ctrl+F "find in conversation" (components/FindInThread.tsx). Opened by the
  // shortcut, or by arriving from a search result (?find=<word>&m=<message id>).
  const [searchParams] = useSearchParams();
  const findParam = searchParams.get('find');
  const threadRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState('thread');
  const [findOpen, setFindOpen] = useState(() => !!findParam);
  const [findFocusTick, setFindFocusTick] = useState(0);
  useEffect(() => {
    if (findParam) {
      setTab('thread');
      setFindOpen(true);
    }
  }, [findParam, id]);

  // Ticket detail is an unscoped route (/tickets/:id) — the URL has no
  // /inbox/:slug so the TopNav InboxSwitcher can't infer the current
  // inbox. Once we load the ticket, stamp its inbox into the last-
  // visited localStorage key. The switcher reads that on every render
  // when the URL is unscoped, so it keeps showing the right chip
  // throughout the user's stay on the ticket detail page (and even on
  // a cold deep-link from a notification or a shared URL).
  const inboxesForStamp = useGmailInboxes();
  useEffect(() => {
    const inboxId = ticketQ.data?.inbox_id;
    if (!inboxId) return;
    const inbox = (inboxesForStamp.data ?? []).find((i) => i.id === inboxId);
    if (inbox?.label_namespace) setLastVisitedSlug(inbox.label_namespace);
  }, [ticketQ.data?.inbox_id, inboxesForStamp.data]);

  // Live presence: who else has this ticket open right now? Returns the
  // OTHER teammates currently subscribed to the presence channel for this
  // ticket — self is filtered out by the hook. Updates within ~100ms of
  // join/leave events via Supabase Realtime.
  const presenceMe = teamMember
    ? {
        slack_user_id: teamMember.slack_user_id,
        name: teamMember.name,
        avatar_url: teamMember.avatar_url ?? null,
        email: teamMember.email,
      }
    : null;
  // "Writing a reply" is announced to the others on this ticket as soon as there is
  // text in the reply box (so two people don't both answer).
  const [drafting, setDrafting] = useState(false);
  const viewers = useTicketPresence(id, presenceMe, drafting);
  const writers = useMemo(() => viewers.filter((v) => v.drafting), [viewers]);

  // Ticket-level keyboard shortcuts. Bindings only fire here (not on the
  // dashboard) because they're wired via this component's lifecycle.
  //
  // The action shortcuts (a / e) go through useTicketAction, which updates
  // the ticket on screen the instant the key is pressed. So the "already mine /
  // already resolved" checks below see the new state on a second press and it
  // is a silent no-op, and repeated presses can't stack requests.
  const { run } = useTicketAction();

  // The list this ticket was opened from: "3 of 57", [ and ], and "open the next
  // one" after Resolve / Snooze / Send. Absent when opened from a link or an email.
  const nav = useTicketNav(id);

  // [ and ] step through the list. On desktop the reply box takes focus when a ticket
  // opens, so they also work from an EMPTY reply box (nothing to lose, and a stray
  // bracket as the first character is rare). Letters never do: an "e" must type.
  const stepKey = (dir: 'next' | 'prev') => (e: KeyboardEvent) => {
    const t = e.target instanceof HTMLElement ? e.target : null;
    const editable =
      !!t && (t.isContentEditable || ['input', 'textarea', 'select'].includes(t.tagName.toLowerCase()));
    if (editable) {
      const inReplyBox = !!t?.closest('[aria-label="Reply body"]');
      if (!inReplyBox || (t?.innerText ?? '').trim() !== '') return;
    }
    e.preventDefault();
    nav.step(dir);
  };

  useKeyboardShortcuts([
    { key: '[', allowInInput: true, handler: stepKey('prev') },
    { key: ']', allowInInput: true, handler: stepKey('next') },
    {
      // Not allowed inside inputs / the reply box (the hook skips those), so
      // there Ctrl+F stays the browser's own find.
      key: 'f',
      ctrl: true,
      handler: (e) => {
        // A dialog owns the keyboard: leave the browser's find alone.
        if (document.querySelector('[role="dialog"]')) return;
        e.preventDefault();
        setTab('thread');
        setFindOpen(true);
        setFindFocusTick((n) => n + 1);
      },
    },
    {
      key: 'r',
      handler: () => {
        if (!canReply) return;
        const el = document.querySelector<HTMLElement>('[role="textbox"][aria-label="Reply body"]');
        el?.focus();
      },
    },
    {
      key: 'n',
      handler: () => {
        const el = document.querySelector<HTMLTextAreaElement>('[data-shortcut-target="ticket-note"]');
        el?.focus();
      },
    },
    {
      key: 'a',
      handler: () => {
        if (!id || !teamMember || !ticketQ.data || !canClaimHere) return;
        // Already mine? Silent no-op so spam-pressing `a` doesn't toast forever.
        if (ticketQ.data.assignee_slack_id === teamMember.slack_user_id) return;
        void run(id, 'claim', undefined, { subject: ticketQ.data.subject });
      },
    },
    {
      key: 'e',
      handler: () => {
        if (!id || !teamMember || !ticketQ.data || !canResolveHere) return;
        // Already resolved/closed? Silent no-op.
        if (ticketQ.data.status === 'resolved' || ticketQ.data.status === 'closed') return;
        void run(id, 'resolve', undefined, { subject: ticketQ.data.subject });
        nav.maybeAdvance();
      },
    },
  ]);

  // Broadcast "I am currently on ticket <id>" to the workspace presence
  // channel so the dashboard can show a small dot on this row for other
  // teammates. Set null on unmount so the dot clears when we leave.
  const { setMyTicket } = useWorkspacePresenceContext();
  useEffect(() => {
    if (!id) return;
    setMyTicket(id);
    return () => setMyTicket(null);
  }, [id, setMyTicket]);

  // If this ticket was merged into another one, find the merge_source event so
  // we can render a banner pointing at the target. Source tickets are
  // auto-archived at merge time (dispatcher sets archived_at + status=closed)
  // and their messages are moved to the target — opening one directly via
  // a notification or search would otherwise show a confusing empty thread
  // with an active composer.
  const mergedInto = useMemo(() => {
    const ev = (eventsQ.data ?? []).find(
      (e) =>
        e.event_type === 'status_changed' &&
        (e.payload as Record<string, unknown> | null)?.kind === 'merge_source',
    );
    if (!ev) return null;
    const p = (ev.payload ?? {}) as Record<string, unknown>;
    const targetId = typeof p.merged_into_ticket_id === 'string' ? p.merged_into_ticket_id : null;
    if (!targetId) return null;
    return {
      targetId,
      targetSubject: typeof p.merged_into_subject === 'string' ? p.merged_into_subject : null,
    };
  }, [eventsQ.data]);

  // When this view mounts (or the id changes), mark any of the signed-in
  // user's unread notifications for this ticket as read. Keeps the bell
  // badge accurate when a user navigates here from Slack/email/anywhere.
  // Fire-and-forget — RLS will no-op if there are none.
  const markMutate = markTicketNotificationsRead.mutate;
  useEffect(() => {
    if (!id) return;
    markMutate(id);
  }, [id, markMutate]);

  // Opening a ticket marks it seen (the bold goes away), and so does a client message
  // arriving while it is open: you are looking at it. "Mark as unread" is not undone by
  // this: the effect only re-runs for a new ticket or a new client message.
  const { markSeen } = useTicketReads();
  const latestInboundAt = useMemo(() => {
    let latest = '';
    for (const m of messagesQ.data ?? []) if (m.direction === 'inbound' && m.created_at > latest) latest = m.created_at;
    return latest;
  }, [messagesQ.data]);
  const ticketLoaded = !!ticketQ.data;
  useEffect(() => {
    if (id && ticketLoaded) markSeen(id);
  }, [id, ticketLoaded, latestInboundAt, markSeen]);

  const teamMembersBySlackId = useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const m of teamMembers ?? []) map.set(m.slack_user_id, m);
    return map;
  }, [teamMembers]);

  // Currently-active away windows team-wide. We pull once at this level
  // so the banner below + the row-level avatar dots (further nested
  // components) share a single cache entry. Polled every 5 min by the
  // hook so opening/closing windows reflect within a few minutes.
  const awayQ = useActiveAwayPeriods();

  // Persistent status bar: shown once the full header scrolls out of
  // view. A zero-height sentinel sits right after the header; when it
  // leaves the top of the viewport (offset by the 44px TopNav), we show
  // the condensed sticky bar. Re-attaches once the ticket (and thus the
  // sentinel) has rendered.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { rootMargin: '-44px 0px 0px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ticketQ.isSuccess]);

  if (!id) {
    return <p className="text-sm text-muted-foreground">No ticket id.</p>;
  }

  if (ticketQ.isLoading) {
    // Shaped skeleton — header skeleton matches the real header's
    // line heights + the thread skeleton renders 4 alternating
    // inbound/outbound bubbles. Layout stays stable as the real
    // ticket hydrates so there's no flash/jump.
    return (
      <div className="flex flex-col gap-4">
        <TicketDetailHeaderSkeleton />
        <MessageThreadSkeleton count={4} />
      </div>
    );
  }

  const ticket = ticketQ.data;
  if (!ticket) {
    return <p className="text-sm text-muted-foreground">Ticket not found.</p>;
  }

  // Thread-jump links — round-trip to Slack / Gmail. They live in the left
  // of the action toolbar (icon-only on phones) rather than in the header, so
  // the header stays short and the toolbar row isn't half empty.
  const threadLinks =
    (ticket.slack_channel_id && ticket.slack_thread_ts) || ticket.gmail_thread_id ? (
      <>
        {ticket.slack_channel_id && ticket.slack_thread_ts ? (
          <ThreadLinkPill
            href={`https://slack.com/archives/${ticket.slack_channel_id}/p${String(
              ticket.slack_thread_ts,
            ).replace('.', '')}`}
            icon={Hash}
            label="Slack"
          />
        ) : null}
        {ticket.gmail_thread_id ? (
          <ThreadLinkPill
            // Use `authuser=<email>` query param so the link opens
            // in the support inbox regardless of where it sits in
            // the user's signed-in Gmail account ordering. The
            // path-segment form `u/<email>/` returns 404 — only
            // `authuser=<email>` actually works. Falls back to
            // `u/0` when the env var isn't configured.
            href={(() => {
              const addr = (import.meta.env.VITE_GMAIL_SUPPORT_ADDRESS as string | undefined) || '';
              return addr
                ? `https://mail.google.com/mail/?authuser=${encodeURIComponent(addr)}#all/${ticket.gmail_thread_id}`
                : `https://mail.google.com/mail/u/0/#all/${ticket.gmail_thread_id}`;
            })()}
            icon={Mail}
            label="Gmail"
          />
        ) : null}
      </>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <Link
          to={nav.ctx && nav.nb ? nav.ctx.returnTo : '/dashboard'}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to tickets
        </Link>
        {nav.ctx && nav.nb ? (
          <TicketListNav nb={nav.nb} ctx={nav.ctx} auto={nav.auto} onAuto={nav.setAuto} onStep={nav.step} />
        ) : null}
      </div>

      {/* Ticket header — Linear/Front-style stacked layout. Subject + status
          chips on the same line; metadata as a tighter row of avatar pills
          below; thread-jump links as ghost buttons at the bottom. */}
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          {/* `title` so a long subject is recoverable on hover. Truncating the
              page's own heading with no way to read the rest meant scrolling
              far enough to trigger TicketStickyBar — which does set `title` —
              just to find out what the ticket was about. */}
          <h1
            className="min-w-0 flex-1 truncate text-xl font-semibold tracking-tight"
            title={ticket.subject ?? undefined}
          >
            {ticket.subject ?? '(no subject)'}
          </h1>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            {viewers.length > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-tint-success-ring bg-tint-success px-2 py-0.5 text-2xs font-medium text-tint-success-fg"
                title={
                  'Also viewing right now: ' +
                  viewers.map((v) => v.name).join(', ')
                }
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tint-success-fg opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-tint-success-fg" />
                </span>
                <span className="hidden items-center gap-1 sm:inline-flex">
                  {writers.length > 0 ? <Pencil className="h-3 w-3" aria-hidden /> : null}
                  {writers.length > 0
                    ? `${writers[0].name} is writing a reply${writers.length > 1 ? ` +${writers.length - 1}` : ''}`
                    : viewers.length === 1
                      ? `${viewers[0].name} is viewing`
                      : `${viewers.length} viewing`}
                </span>
                <span className="-space-x-1.5">
                  {viewers.slice(0, 3).map((v) => (
                    <span
                      key={v.slack_user_id}
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-tint-success ring-1 ring-tint-success-ring"
                      title={v.name}
                    >
                      {v.avatar_url ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={v.avatar_url}
                          alt={v.name}
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <span className="text-3xs font-semibold text-tint-success-fg">
                          {v.name
                            .split(/\s+/)
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((s) => s[0]?.toUpperCase() ?? '')
                            .join('')}
                        </span>
                      )}
                    </span>
                  ))}
                  {viewers.length > 3 ? (
                    <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-tint-success px-1 text-3xs font-semibold text-tint-success-fg ring-1 ring-tint-success-ring">
                      +{viewers.length - 3}
                    </span>
                  ) : null}
                </span>
              </span>
            ) : null}
            <PriorityMenu ticket={ticket} />
            <Badge variant="pill" className={statusColor(ticket.status)}>
              {(ticket.status ?? '—').replace(/_/g, ' ')}
            </Badge>
            {ticket.category ? (
              <Badge variant="secondary">{ticket.category}</Badge>
            ) : null}
            <SlaCountdown ticket={ticket} />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
          {/* Sender — avatar + name + email link to contact.
              max-w on the link + min-w-0/truncate on the inner name so an
              "Acme Veterinary Hospital & Long Clinic Name Pty Ltd" client
              can't push the assignee pill off the right edge on a 360px
              phone. The avatar stays shrink-0 so it never gets squashed. */}
          {ticket.sender_email && ticket.sender_email.trim() ? (
            <Link
              to={`/contacts/view?email=${encodeURIComponent(ticket.sender_email)}`}
              className="group inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-transparent px-1.5 py-0.5 transition-colors hover:border-border hover:bg-accent sm:max-w-[16rem]"
              title={ticket.sender_email}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tint-info text-3xs font-medium text-tint-info-fg">
                {nameInitials(ticket.sender_name ?? ticket.sender_email ?? '?')}
              </span>
              <span className="min-w-0 truncate font-medium text-foreground/85 group-hover:text-foreground">
                {ticket.sender_name ?? ticket.sender_email}
              </span>
            </Link>
          ) : (
            <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 px-1.5 py-0.5 sm:max-w-[16rem]">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tint-neutral text-3xs font-medium text-tint-neutral-fg">
                {nameInitials(ticket.sender_name ?? '?')}
              </span>
              <span className="min-w-0 truncate font-medium text-foreground/85">
                {ticket.sender_name ?? 'Unknown sender'}
              </span>
            </span>
          )}

          <span aria-hidden className="select-none text-muted-foreground/70">→</span>

          {/* Assignee — UserAvatar with the team_member's photo.
              min-w-0 + truncate on the name so a long team-member name
              ("Christina-Renee Westhampton") can't push the rest of the
              metadata row off the right edge on phones. */}
          {ticket.assignee_slack_id ? (
            <span className="inline-flex min-w-0 max-w-[12rem] items-center gap-1.5 rounded-full border border-transparent px-1.5 py-0.5">
              <UserAvatar
                slackId={ticket.assignee_slack_id}
                teamMembersBySlackId={teamMembersBySlackId}
                size="sm"
              />
              <span className="min-w-0 truncate font-medium text-foreground/85">
                {assigneeName(ticket.assignee_slack_id, teamMembersBySlackId)}
              </span>
            </span>
          ) : (
            <span className="rounded-full border border-dashed border-border px-2 py-0.5 italic text-muted-foreground">
              Unassigned
            </span>
          )}

          <span aria-hidden className="select-none text-muted-foreground/70">·</span>
          {/* Opened-at: relative ("2h ago") for skim, full local
              datetime on hover for precision. Same pattern reused on
              every message bubble below. */}
          <span className="tabular-nums">
            Opened <RelativeTime value={ticket.created_at} />
          </span>

          {ticket.client_tier ? (
            <>
              <span aria-hidden className="select-none text-muted-foreground/70">·</span>
              <span className="uppercase tracking-wide text-muted-foreground">
                {ticket.client_tier}
              </span>
            </>
          ) : null}

          <span aria-hidden className="select-none text-muted-foreground/70">·</span>
          <AutomatedSenderControl senderEmail={ticket.sender_email} robot={ticket.is_automated_sender === true} />
        </div>

        {/* The AI's one-line read of what the client wants, and a heads-up when the
            tone is not calm. (Both were captured at triage but never shown.) */}
        {ticket.intent_summary?.trim() ? (
          <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
            <Sparkles className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
            <span className="min-w-0">
              <span className="font-medium text-foreground/80">What they want:</span> {ticket.intent_summary}
              {ticket.sentiment === 'negative' || ticket.sentiment === 'frustrated' || ticket.sentiment === 'angry' ? (
                <Badge variant="pill" className="ml-2 bg-tint-warning text-tint-warning-fg">
                  {ticket.sentiment} tone
                </Badge>
              ) : null}
            </span>
          </p>
        ) : null}

        {/* Sentinel: when this scrolls above the TopNav line, the toolbar
            switches to its condensed (subject + status) form. Zero-size and
            absolutely positioned so it adds no layout gap. */}
        <div ref={sentinelRef} aria-hidden className="absolute inset-x-0 bottom-0 h-0" />
      </div>

      {/* Pinned action toolbar — always on screen, right under the TopNav.
          Holds every ticket action (Assign / Resolve / Snooze / Escalate /
          Live assist / ⋯). Once the full header above has scrolled out of
          view it also shows subject + status/priority/SLA (`condensed`). */}
      <TicketStickyBar
        ticket={ticket}
        teamMembersBySlackId={teamMembersBySlackId}
        awayPeriods={awayQ.data}
        condensed={showStickyBar}
        leading={threadLinks}
        onActionComplete={(action) => {
          if (advancesList(action)) nav.maybeAdvance();
        }}
      />

      {/*
        Two-column layout on lg+ screens (≥1024px):
          - Left: Thread + Summary tabs (main column).
          - Right: Watchers → Notes → Attachments → From this contact →
            Activity, sticky to the top under the nav. Each section is
            height-capped with a "Show more" toggle (see RailSection).
        Below lg, falls back to a single column where the right rail stacks
        below the thread. Ticket actions live in the pinned toolbar above,
        not in the rail.
      */}
      {/* Away-coverage banner — shown when the assignee is currently
          inside one of their team_away_periods. We render this above
          the snooze/merge banners because "the person you'd expect to
          handle this is off; here's who to ping instead" is the most
          time-sensitive piece of context on the page. The lookup is
          O(1) via the active-away Map keyed by slack_user_id. */}
      {(() => {
        if (!ticket.assignee_slack_id || !awayQ.data) return null;
        const period = awayQ.data.get(ticket.assignee_slack_id);
        if (!period) return null;
        const awayMember = teamMembersBySlackId.get(ticket.assignee_slack_id);
        const backupMember = teamMembersBySlackId.get(period.backup_slack_id);
        return (
          <AwayBanner
            period={period}
            awayName={awayMember?.name ?? awayMember?.email ?? ticket.assignee_slack_id}
            backupName={backupMember?.name ?? backupMember?.email ?? period.backup_slack_id}
            teamMembersBySlackId={teamMembersBySlackId}
          />
        );
      })()}

      {/* Snooze banner — only visible while the ticket is in its snooze
          window. The auto-resurface pg_cron clears `snoozed_until` within
          5 min of expiry; we re-check the timestamp at render so an
          expired-but-not-yet-cleared row doesn't show a stale banner.
          Sits ABOVE the merge banner because a merged-out ticket can't
          also be snoozed in practice (merge auto-archives the source),
          but if it ever did the merge banner is more important. */}
      {ticket.snoozed_until && new Date(ticket.snoozed_until) > new Date() && !mergedInto ? (
        <div className="mb-3 flex items-start gap-3 rounded-md border border-tint-active-ring bg-tint-active px-3 py-2.5 text-sm text-tint-active-fg">
          <Moon className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              Snoozed until{' '}
              {new Date(ticket.snoozed_until).toLocaleString(undefined, {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
            <p className="mt-0.5 text-xs text-tint-active-fg/80">
              Hidden from the dashboard default views; SLA alerts are paused. It'll come back automatically when the timer expires — or click <strong>Wake up</strong> in the actions bar to bring it back now.
            </p>
          </div>
        </div>
      ) : null}

      {mergedInto ? (
        <div className="flex items-start gap-3 rounded-md border border-tint-warning-ring bg-tint-warning px-3 py-2.5 text-sm text-tint-warning-fg">
          <GitMerge className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">This ticket was merged into another one.</p>
            <p className="mt-0.5 text-xs text-tint-warning-fg/80">
              Its messages now live on{' '}
              <Link
                to={`/tickets/${mergedInto.targetId}`}
                className="font-medium underline underline-offset-2 hover:text-tint-warning-fg"
              >
                {mergedInto.targetSubject ?? 'the target ticket'}
              </Link>
              . Replies should be sent from there.
            </p>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div className="min-w-0 flex-1">
          <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-3">
            <TabsList>
              <TabsTrigger value="thread">Thread</TabsTrigger>
              <TabsTrigger value="summary">Summary</TabsTrigger>
            </TabsList>

            <TabsContent ref={threadRef} value="thread" className="flex flex-col gap-3">
              {findOpen ? (
                <FindInThread
                  key={`${id}:${findParam ?? ''}`}
                  containerRef={threadRef}
                  initialQuery={findParam ?? ''}
                  focusMessageId={searchParams.get('m')}
                  refreshKey={(messagesQ.data ?? []).length}
                  focusTick={findFocusTick}
                  onClose={() => setFindOpen(false)}
                />
              ) : null}
              {messagesQ.isLoading ? (
                <MessageThreadSkeleton count={3} />
              ) : (messagesQ.data ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No messages yet.</p>
              ) : (
                (messagesQ.data ?? []).map((m) => {
                  const inbound = m.direction === 'inbound';
                  // Resolve display info — for outbound, prefer the team_member
                  // by slack_user_id match against the from_email's local-part
                  // is messy; we just match on email exact-equals.
                  const teamMember = !inbound
                    ? (teamMembers ?? []).find(
                        (tm) => tm.email.toLowerCase() === (m.from_email ?? '').toLowerCase(),
                      )
                    : null;
                  const displayName = inbound
                    ? (ticket.sender_name ?? m.from_email ?? 'Client')
                    : (teamMember?.name ?? m.from_email ?? 'Team');
                  const displayEmail = m.from_email ?? '';
                  const initials = nameInitials(displayName) || nameInitials(displayEmail);

                  // Strong direction cue — each message wears a chunky
                  // 4px left-edge stripe (blue for client, emerald for
                  // team) plus a faint bg tint of the same hue. The
                  // stripe is unmistakable in a long thread: you can
                  // scan it like a chat transcript without having to
                  // read the avatar/name first.
                  return (
                    <article
                      key={m.id}
                      data-message-id={m.id}
                      className={cn(
                        // min-w-0 + overflow-hidden contain anything inside
                        // the bubble — long email addresses in the header,
                        // wide HTML tables in the body, oversized inline
                        // images — so the bubble can never push the article
                        // wider than its grid column on mobile.
                        'min-w-0 overflow-hidden rounded-lg border border-l-4 bg-background',
                        inbound
                          ? 'border-tint-info-ring/60 border-l-blue-400 bg-tint-info/20'
                          : 'border-tint-success-ring/60 border-l-emerald-500 bg-tint-success/30',

                        // ── Dark mode: the WHOLE message is a light sheet ────
                        // Email HTML carries the sender's own inline colours,
                        // written for a white background, so it can't be
                        // recoloured reliably — it has to sit on a light
                        // surface. The first attempt put a white card around
                        // just the body, which left a hard white block floating
                        // inside a dark bubble: an obvious seam, and the
                        // brightest thing on the page.
                        //
                        // The whole message becomes the sheet instead, header
                        // included, so a thread reads as a stack of paper on a
                        // dark desk rather than dark cards with white holes in
                        // them. Soft neutral (#f1f5f9) rather than pure white —
                        // a full-brightness sheet is fatiguing to read against
                        // a dark UI, and the sender's dark text has ample
                        // contrast on it either way.
                        'dark:border-slate-300 dark:bg-slate-100 dark:text-slate-900',
                        inbound ? 'dark:border-l-blue-500' : 'dark:border-l-emerald-600',
                      )}
                    >
                      <header className="flex items-start gap-3 px-3.5 pt-3">
                        {/* Avatar — UserAvatar for team, initials chip for clients */}
                        {!inbound && teamMember ? (
                          <UserAvatar
                            slackId={teamMember.slack_user_id}
                            teamMembersBySlackId={teamMembersBySlackId}
                            size="md"
                          />
                        ) : (
                          <span
                            className={cn(
                              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-2xs font-medium',
                              inbound
                                ? 'bg-tint-info text-tint-info-fg dark:bg-blue-100 dark:text-blue-800'
                                : 'bg-tint-success text-tint-success-fg dark:bg-emerald-100 dark:text-emerald-800',
                            )}
                            title={displayEmail}
                          >
                            {initials}
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="min-w-0 truncate text-sm font-semibold text-foreground dark:text-slate-900">
                              {displayName}
                              {inbound ? (
                                <span className="ml-1.5 align-baseline text-3xs font-medium uppercase tracking-wide text-tint-info-fg/80 dark:text-blue-700">
                                  client
                                </span>
                              ) : null}
                            </p>
                            {/* Message bubble timestamp: shows the
                                relative form ("5m ago", "2h ago") for
                                instant skimming; hover shows the full
                                local datetime. Reading a long thread
                                no longer requires mental TZ math. */}
                            <RelativeTime
                              value={m.created_at}
                              className="shrink-0 text-2xs text-muted-foreground dark:text-slate-500"
                            />
                          </div>
                          {/* break-all so a long single email address
                              wraps mid-string instead of forcing the
                              whole bubble width; max-w-full keeps it
                              from blowing past the article container
                              when an inbound message has 5+ ccs. */}
                          <p className="max-w-full break-all text-2xs text-muted-foreground dark:text-slate-500">
                            {displayEmail}
                            {m.to_emails && m.to_emails.length > 0 ? (
                              <span> · to {m.to_emails.join(', ')}</span>
                            ) : null}
                            {m.cc_emails && m.cc_emails.length > 0 ? (
                              <span> · cc {m.cc_emails.join(', ')}</span>
                            ) : null}
                          </p>
                        </div>
                      </header>
                      <div className="px-3.5 py-3">
                        <MessageBody
                          body={m.body ?? '(no body)'}
                          bodyHtml={m.body_html}
                          attachments={m.attachments}
                          messageId={m.id}
                          variant={inbound ? 'inbound' : 'outbound'}
                        />
                      </div>
                    </article>
                  );
                })
              )}
              {/* Replies you've sent that are still going out (or didn't):
                  the composer clears on Send, and the message waits here. */}
              {id ? (
                <PendingReplies ticketId={id} teamMembersBySlackId={teamMembersBySlackId} />
              ) : null}
            </TabsContent>

            <TabsContent value="summary">
              <ThreadSummary
                ticketId={id ?? ''}
                latestMessageId={messagesQ.data?.[messagesQ.data.length - 1]?.id}
                messageCount={messagesQ.data?.length ?? 0}
              />
            </TabsContent>

            {/* The "KB Matches" tab used to live here, saying the feature was
                "surfaced here in a future iteration" — while `KbSuggestions`
                renders live, clickable KB matches inside the composer a few
                hundred pixels below. One of three top-level tabs on the primary
                working surface was telling agents a shipped feature didn't
                exist. Removed rather than filled in: the composer is the right
                place for it, because that's where you're writing the reply. */}
          </Tabs>

          {/* Reply composer — sits at the natural end of the thread and
              scrolls *with* the page (not pinned to the viewport bottom).
              Earlier the composer was `sticky bottom-0`, which meant a
              tall paste / long draft grew the bar upwards and covered
              the thread. Now the whole page scrolls together: paste a
              huge image, the page just gets taller, and you can scroll
              up to read the thread above. Hidden entirely when this
              ticket was merged into another one — replies should go
              from the target ticket, not this husk. */}
          {!mergedInto && canReply ? (
            <div className="-mx-1 mt-2 border-t border-border/70 px-1 py-3">
              <ReplyComposer
                ticketId={ticket.id}
                onSent={nav.maybeAdvance}
                onDraftingChange={setDrafting}
                othersDrafting={writers.map((v) => v.name)}
              />
            </div>
          ) : !mergedInto && !access.isLoading ? (
            <div
              role="status"
              className="-mx-1 mt-2 rounded-md border border-border/70 bg-muted/30 px-3 py-3 text-sm text-muted-foreground"
            >
              You can view this ticket but not reply to it
              {access.roleName ? ` (your role: ${access.roleName})` : ''}. Ask an owner or admin if you need to.
            </div>
          ) : null}
        </div>

        {/* Right rail: Watchers → Notes → Attachments → From this contact →
            Activity. Watchers is first so adding a watcher never needs a
            scroll; every section is height-capped (RailSection) so a long
            thread can't turn the rail into an endless scroll. Sticky on lg+
            so it stays visible while the thread scrolls. Top offset clears
            the sticky <TopNav> (44px) + the pinned action toolbar (~44px).

            The internal scrollbar (kicks in when content is taller than
            the viewport) is hidden visually via `scrollbar-width:none`
            (Firefox) + `::-webkit-scrollbar` (Chromium/Safari). Scrolling
            still works — touchpad, two-finger drag, scrollwheel-over-rail
            all work — there's just no chrome cluttering the rail edge. */}
        <aside className="flex w-full min-w-0 flex-col gap-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:w-80 lg:shrink-0 lg:overflow-y-auto lg:[scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TicketWatchersPanel ticketId={ticket.id} />
          <NotesPanel ticketId={ticket.id} />
          <ThreadAttachmentsPanel ticketId={ticket.id} />
          {/* Other tickets from the same contact — "have I dealt with this
              person before?" context. Excludes the current ticket. */}
          <ContactTicketsPanel email={ticket.sender_email} currentTicketId={ticket.id} />
          <TicketActivityPanel ticketId={ticket.id} />
        </aside>
      </div>
    </div>
  );
}

// External thread-jump pill (Slack / Gmail). Pure visual — no app logic.
function ThreadLinkPill({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open in ${label}`}
      title={`Open in ${label}`}
      // Icon-only 32px circle on phones (the toolbar is tight there), labelled
      // pill from sm up.
      className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border border-border bg-background px-2.5 text-2xs font-medium text-muted-foreground transition-all hover:border-foreground/30 hover:bg-accent hover:text-foreground hover:shadow-sm max-sm:h-8 max-sm:w-8 max-sm:justify-center max-sm:px-0"
    >
      <Icon className="h-3 w-3 max-sm:h-4 max-sm:w-4" />
      <span className="max-sm:sr-only">Open in {label}</span>
    </a>
  );
}
